import { Suspense, useState } from 'react';
import * as Avatar from '@givebutter/primitives/avatar';
import { Banner } from '@givebutter/primitives/banner';
import { Button } from '@givebutter/primitives/button';
import { SkeletonBar } from '@givebutter/primitives/skeleton';
import { faBars, faRightFromBracket, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { show as showIntercomChat } from '@intercom/messenger-js-sdk';
import * as DropdownMenu from 'components/dropdown-menu';
import { PermissionErrorBoundary } from 'components/error-boundaries/PermissionErrorBoundary';
import { HeaderButton } from 'components/header/HeaderButton';
import { HeaderLogo, HeaderLogoFallback } from 'components/header/HeaderLogo';
import { HelpMenu } from 'components/header/HelpMenu';
import { NucleoIcon } from 'components/nucleo-icon';
import { Search } from 'components/search/Search';
import { TasksDrawer } from 'components/tasks/TasksDrawer';
import { IntercomProductTourTargets } from 'constants/intercom';
import { TestIds } from 'constants/tests';
import { useResponsive } from 'contexts/useResponsive';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { useLogout } from 'customHooks/useLogout';

const Header = () => {
    const currentUser = useCurrentUser();
    const apiUrl = useAppState(state => state.apiUrl);
    const { mutate: logout } = useLogout();
    const { isMobile, isSidebarCollapsed, setIsSidebarCollapsed } = useResponsive();

    return (
        <>
            <header
                data-testid={TestIds.Global.Header}
                className="z-pentOutermost fixed top-0 flex w-full items-center justify-between gap-0 bg-gray-900 px-6 py-3 sm:gap-4"
            >
                <div className="flex items-center gap-4">
                    {isMobile ? (
                        <Button
                            className="h-[1rem] rounded-none text-white !transition-none"
                            size="lg"
                            variant="transparent"
                            shape={'square'}
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            aria-label="Menu"
                        >
                            <FontAwesomeIcon icon={faBars} size="lg" />
                        </Button>
                    ) : null}
                    <Suspense fallback={<HeaderLogoFallback />}>
                        <HeaderLogo />
                    </Suspense>
                </div>
                <Suspense fallback={null}>
                    <SearchWrapper />
                </Suspense>
                <div className="flex h-full flex-wrap items-center">
                    <div className="flex h-full flex-wrap items-center gap-2 sm:gap-3">
                        <HelpMenu />

                        {!isMobile && (
                            <HeaderButton onClick={() => showIntercomChat()}>
                                <NucleoIcon icon="message" />
                                Chat
                            </HeaderButton>
                        )}

                        <PermissionErrorBoundary FallbackComponent={null}>
                            <Suspense fallback={<SkeletonBar className="!w-20" />}>
                                <TasksDrawer />
                            </Suspense>
                        </PermissionErrorBoundary>

                        <DropdownMenu.Root>
                            <DropdownMenu.Trigger asChild={true}>
                                <Button
                                    variant="transparent"
                                    data-intercom-target={IntercomProductTourTargets.HeaderUserMenu}
                                    size="md"
                                    shape="square"
                                    className="flex-shrink-0 overflow-hidden rounded-full !p-0"
                                    aria-label="User Menu"
                                >
                                    <Avatar.Root size="xs">
                                        <Avatar.Image
                                            src={currentUser.picture ?? undefined}
                                            alt="Profile picture"
                                        />
                                        <Avatar.Fallback aria-label="Profile picture">
                                            {currentUser.firstName[0]}
                                        </Avatar.Fallback>
                                    </Avatar.Root>
                                </Button>
                            </DropdownMenu.Trigger>

                            <DropdownMenu.Content sideOffset={5} align="end">
                                <DropdownMenu.Item asChild>
                                    <a
                                        href={`${apiUrl}/profile`}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        className="flex items-center gap-2 text-gray-900 focus-visible:before:border-0"
                                    >
                                        <FontAwesomeIcon icon={faUser} />
                                        My Profile
                                    </a>
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                    onClick={() => logout()}
                                    className="flex items-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faRightFromBracket} />
                                    Logout
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    </div>
                </div>
            </header>

            <Suspense fallback={null}>
                <HeaderAlerts />
            </Suspense>
        </>
    );
};

const SearchWrapper = () => {
    const { search_api_key } = useCurrentAccount();

    if (!search_api_key) {
        return null;
    }

    return <Search />;
};

const HeaderAlerts = () => {
    const [showBlockedBanner, setShowBlockedBanner] = useState(true);
    const currentAccount = useCurrentAccount();

    return (
        <>
            {currentAccount.arePaymentsBlocked && showBlockedBanner && (
                <Banner type="error" dismissable onDismiss={() => setShowBlockedBanner(false)}>
                    <span>
                        Please{' '}
                        <Button
                            variant="none"
                            color="primary"
                            className="font-semibold underline"
                            onClick={() => showIntercomChat()}
                        >
                            contact us
                        </Button>{' '}
                        to activate your account and begin accepting payments.
                    </span>
                </Banner>
            )}
        </>
    );
};

export { Header };
